import { FLANGE } from "@/config/designer";
import type { SampleRegion } from "./brushes";
import type { VoxelField } from "./VoxelField";

export interface FlangeResult {
  applied: boolean;
  addedSamples: number;
  region: SampleRegion | null;
  reason?: string;
}

/**
 * Union a flat-bottomed, smoothly ramped ring into the current field.
 *
 * The ring grows only into exterior empty space connected to the outside of
 * the footprint, so pockets inside a hold are never accidentally filled. A
 * chamfer distance transform supplies a stable millimetre distance from the
 * current footprint without an expensive all-pairs search.
 */
export function applyFlange(field: VoxelField, requestedWidth: number): FlangeResult {
  const width = clamp(requestedWidth, FLANGE.minWidth, FLANGE.maxWidth);
  const { nx, ny, nz, h, data, dirty } = field;
  const planeSize = nx * nz;
  const occupied = new Uint8Array(planeSize);
  const exterior = new Uint8Array(planeSize);
  const distance = new Float32Array(planeSize);
  const surfaceHeight = new Float32Array(planeSize);
  const nearest = new Int32Array(planeSize);
  nearest.fill(-1);
  const index2 = (i: number, k: number): number => i + nx * k;

  const probeY = Math.min(field.height - h * 0.5, Math.max(FLANGE.floorThickness, h * 1.5));
  const probeJ = clampInt(Math.round((probeY - field.oy) / h), 0, ny - 1);
  let occupiedCount = 0;
  let occupiedMinX = Infinity;
  let occupiedMaxX = -Infinity;
  let occupiedMinZ = Infinity;
  let occupiedMaxZ = -Infinity;
  for (let k = 0; k < nz; k++) {
    const z = field.sampleZ(k);
    for (let i = 0; i < nx; i++) {
      const p = index2(i, k);
      if ((data[field.index(i, probeJ, k)] as number) < 0) {
        occupied[p] = 1;
        nearest[p] = p;
        occupiedCount++;
        const x = field.sampleX(i);
        occupiedMinX = Math.min(occupiedMinX, x);
        occupiedMaxX = Math.max(occupiedMaxX, x);
        occupiedMinZ = Math.min(occupiedMinZ, z);
        occupiedMaxZ = Math.max(occupiedMaxZ, z);
        for (let j = ny - 1; j >= probeJ; j--) {
          if ((data[field.index(i, j, k)] as number) < 0) {
            surfaceHeight[p] = Math.min(field.height, field.sampleY(j) + h * 0.5);
            break;
          }
        }
      }
    }
  }
  if (occupiedCount === 0) {
    return { applied: false, addedSamples: 0, region: null, reason: "No base footprint remains." };
  }
  const spansStartingBlank =
    occupiedMinX <= h &&
    occupiedMaxX >= field.width - h &&
    occupiedMinZ <= h &&
    occupiedMaxZ >= field.depth - h;

  // Mark only empty space connected to the outside border. Enclosed pockets
  // stay untouched even when they lie within the requested flange width.
  const queue = new Int32Array(planeSize);
  let head = 0;
  let tail = 0;
  const enqueue = (i: number, k: number): void => {
    const p = index2(i, k);
    if (occupied[p] || exterior[p]) return;
    exterior[p] = 1;
    queue[tail++] = p;
  };
  for (let i = 0; i < nx; i++) {
    enqueue(i, 0);
    enqueue(i, nz - 1);
  }
  for (let k = 1; k < nz - 1; k++) {
    enqueue(0, k);
    enqueue(nx - 1, k);
  }
  while (head < tail) {
    const p = queue[head++] as number;
    const i = p % nx;
    const k = Math.floor(p / nx);
    if (i > 0) enqueue(i - 1, k);
    if (i + 1 < nx) enqueue(i + 1, k);
    if (k > 0) enqueue(i, k - 1);
    if (k + 1 < nz) enqueue(i, k + 1);
  }

  distance.fill(Number.POSITIVE_INFINITY);
  for (let p = 0; p < planeSize; p++) if (occupied[p]) distance[p] = 0;
  const diagonal = h * Math.SQRT2;
  const adopt = (p: number, source: number, step: number): void => {
    const candidate = (distance[source] as number) + step;
    if (candidate < (distance[p] as number)) {
      distance[p] = candidate;
      nearest[p] = nearest[source] as number;
    }
  };
  for (let k = 0; k < nz; k++) {
    for (let i = 0; i < nx; i++) {
      const p = index2(i, k);
      if (i > 0) adopt(p, p - 1, h);
      if (k > 0) adopt(p, p - nx, h);
      if (i > 0 && k > 0) adopt(p, p - nx - 1, diagonal);
      if (i + 1 < nx && k > 0) adopt(p, p - nx + 1, diagonal);
    }
  }
  for (let k = nz - 1; k >= 0; k--) {
    for (let i = nx - 1; i >= 0; i--) {
      const p = index2(i, k);
      if (i + 1 < nx) adopt(p, p + 1, h);
      if (k + 1 < nz) adopt(p, p + nx, h);
      if (i + 1 < nx && k + 1 < nz) adopt(p, p + nx + 1, diagonal);
      if (i > 0 && k + 1 < nz) adopt(p, p + nx - 1, diagonal);
    }
  }

  let addedSamples = 0;
  let region: SampleRegion | null = null;

  for (let k = 0; k < nz; k++) {
    const z = field.sampleZ(k);
    for (let i = 0; i < nx; i++) {
      const x = field.sampleX(i);
      const p2 = index2(i, k);
      const signedDistance = spansStartingBlank
        ? signedRectangleDistance(x, z, field.width, field.depth)
        : (distance[p2] as number);
      const d = Math.max(0, signedDistance);
      const eligible = spansStartingBlank
        ? signedDistance <= width + h
        : exterior[p2] && Number.isFinite(d) && d <= width + h;
      if (!eligible) continue;

      const inward = 1 - clamp(d / width, 0, 1);
      const eased = smootherStep(inward);
      const source = nearest[p2] as number;
      const joinHeight = source >= 0
        ? Math.max(FLANGE.floorThickness, surfaceHeight[source] as number)
        : FLANGE.floorThickness;
      // The ramp reaches the actual local surface height. This removes the
      // vertical crack/ledge left by a fixed-height shoulder and gives the
      // union enough overlap for a smooth clay-like join.
      const top = FLANGE.floorThickness + (joinHeight - FLANGE.floorThickness) * eased;

      for (let j = 0; j < ny; j++) {
        const y = field.sampleY(j);
        if (y < -h || y > top + h) continue;
        const p = field.index(i, j, k);
        const previous = data[p] as number;
        const flangeDistance = Math.max(signedDistance - width, y - top, -y);
        const next = field.clampValue(
          smoothMin(previous, flangeDistance, FLANGE.joinBlendRadius),
        );
        if (next >= previous - 1e-7) continue;
        data[p] = next;
        dirty[p] = 1;
        addedSamples++;
        if (!region) {
          region = { i0: i, i1: i + 1, j0: j, j1: j + 1, k0: k, k1: k + 1 };
        } else {
          region.i0 = Math.min(region.i0, i);
          region.i1 = Math.max(region.i1, i + 1);
          region.j0 = Math.min(region.j0, j);
          region.j1 = Math.max(region.j1, j + 1);
          region.k0 = Math.min(region.k0, k);
          region.k1 = Math.max(region.k1, k + 1);
        }
      }
    }
  }

  return addedSamples > 0
    ? { applied: true, addedSamples, region }
    : {
        applied: false,
        addedSamples: 0,
        region: null,
        reason: "There is no free space around the base for a flange.",
      };
}

function smootherStep(value: number): number {
  const t = clamp(value, 0, 1);
  return t * t * t * (t * (t * 6 - 15) + 10);
}

function smoothMin(a: number, b: number, radius: number): number {
  if (radius <= 0) return Math.min(a, b);
  const h = clamp(0.5 + (0.5 * (b - a)) / radius, 0, 1);
  return b + (a - b) * h - radius * h * (1 - h);
}

function clamp(value: number, min: number, max: number): number {
  return value < min ? min : value > max ? max : value;
}

function clampInt(value: number, min: number, max: number): number {
  return Math.round(clamp(value, min, max));
}

function signedRectangleDistance(
  x: number,
  z: number,
  width: number,
  depth: number,
): number {
  const outsideX = Math.max(-x, 0, x - width);
  const outsideZ = Math.max(-z, 0, z - depth);
  const outside = Math.hypot(outsideX, outsideZ);
  if (outside > 0) return outside;
  return -Math.min(x, width - x, z, depth - z);
}
